import React, { useState } from 'react'
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '../../../../components/ui/sheet'
import { Button } from '../../../../components/ui/button'
import { PlusIcon } from 'lucide-react'
import { Label } from '../../../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select'
import { useInterventionInput } from '../../../../context/InterventionInputContext'
import { Input } from '../../../../components/ui/input'
import Signature from '../../../../components/ui/signature'
import { Switch } from '../../../../components/ui/switch'
import { format } from 'date-fns'
import { z } from 'zod'
import { useBoundStore } from '../../../../zustand'
import { Trans, useLingui } from '@lingui/react/macro'

const AddBTWAttest = ({onSuccess}:{onSuccess:()=>void}) => {
    const { t } = useLingui()
    const { intervention, setbtw_attest } = useInterventionInput()

    const documents = useBoundStore((state) => state.documents)
    
    const [language, setlanguage] = useState<string>('N');
    const [created_in, setcreated_in] = useState<string>(intervention?.m_oKLANT.m_sSTAD || '');
    const [signature, setsignature] = useState<string>('');

    function isComplete () {
        try {
            z.object({
                created_in: z.string().min(1),
                signature: z.string().min(2000),
            }).parse({
                created_in: created_in,
                signature: signature,
            })
            return true
        } catch (e) {
            return false
        }
    }

    function handleAddBTWAttest () {                
        setbtw_attest([{
            created: created_in,
            language: language,
            datecreate: format(new Date(), "dd/MM/yyyy"),
            customerid: '',
            customername: '',
            docid: '',
            interventionid: intervention?.m_iINTERVENTIESID || '',
            machineid: '',
            price: '',
            sku: '',
            soort: 'BTWATTEST',
            sent: 0,
            type: 'BTWATTEST',
            usercreate: '',
            handtekeningklant: signature.split('base64,')[1] || signature
        }])

        onSuccess&&onSuccess()

        setsignature('');
        setcreated_in('');
    }

    const btwDocument = Array.isArray(documents) ? documents?.find((doc)=>doc.m_SOORT === 'BTWATTEST') : undefined

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button><Trans>Nieuw 6% BTW attest</Trans></Button>
            </SheetTrigger>
            <SheetContent className='w-4/5 !max-w-[100vw] max-h-dvh overflow-auto'>
                <div className='flex flex-col gap-4'>
                    <div>
                        <Label><Trans>Taal</Trans></Label>
                        <Select value={language} onValueChange={(v)=>{setlanguage(v)}}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value='N'><Trans>Nederlands</Trans></SelectItem>
                                <SelectItem value='F'><Trans>Frans</Trans></SelectItem>
                                <SelectItem value='E'><Trans>Engels</Trans></SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div>
                        <p className='text-xl font-semibold'><Trans>Ondergetekende</Trans></p>
                        
                        <div>
                            <Label><Trans>Naam</Trans></Label>
                            <p>{intervention?.m_oKLANTFACTURATIE?.m_sNAAM}</p>
                        </div>
                        <div>
                            <Label><Trans>Adres</Trans></Label>
                            <p>{intervention?.m_oKLANTFACTURATIE.m_sADRES1} {intervention?.m_oKLANTFACTURATIE?.m_sBUS}, {intervention?.m_oKLANTFACTURATIE.m_sPOSTCODE} {intervention?.m_oKLANTFACTURATIE.m_sSTAD}</p>
                            <p>{intervention?.m_oKLANTFACTURATIE.m_sADRES2}</p>
                        </div>
                    </div>
                    
                    <div>
                        <p className='text-xl font-semibold'><Trans>Verklaart dat het gebouw gelegen</Trans></p>
                        <div>
                            <Label><Trans>Adres</Trans></Label>
                            <p>{intervention?.m_oKLANT.m_sADRES1} {intervention?.m_oKLANT.m_sNR} {intervention?.m_oKLANT?.m_sBUS}, {intervention?.m_oKLANT.m_sPOSTCODE} {intervention?.m_oKLANT.m_sSTAD}</p>
                            <p>{intervention?.m_oKLANT.m_sADRES2}</p>
                        </div>
                    </div>
                    <div>
                        <p>{btwDocument?.m_TEKST || t`Geen tekst gevonden`}</p>
                    </div>
                    <div>
                        <Label><Trans>Opgemaakt in</Trans></Label>
                        <Input value={created_in} onChange={(e)=>{setcreated_in(e.currentTarget.value)}}/>
                    </div>
                    <p><Trans>op</Trans> {format(new Date(), "dd/MM/yyyy")}</p>
                    <div>
                        <Signature onChange={(signature)=>{setsignature(signature)}} label={t`Handtekening klant`}/>
                    </div>
                    <SheetClose>
                        <Button onClick={()=>{handleAddBTWAttest()}} disabled={!isComplete()}><Trans>Opmaken en toevoegen</Trans></Button>
                    </SheetClose>
                </div>
            </SheetContent>
        </Sheet>
    )
}

const AddDocuments = () => {
    const { btw_attest } = useInterventionInput()

    const [open, setopen] = useState(false);
    const [older_10years, setolder_10years] = useState(true);
        
  return (
    <Sheet open={open} onOpenChange={setopen}>
        <SheetTrigger asChild>
            <Button><PlusIcon className='w-4 h-4'/><Trans>Documenten toevoegen</Trans></Button>
        </SheetTrigger>
        <SheetContent className='w-4/5 !max-w-[100vw]'>
            <div className='flex flex-col gap-4'>
                <div>
                    <p className='text-xl font-semibold'><Trans>Onderhoudscontract</Trans></p>
                    <p><Trans>Gelieve een onderhoudscontract te suggereren aan de klant...</Trans></p>
                </div>
                <div className='space-y-2'>
                    <p className='text-xl font-semibold'><Trans>BTW-attest</Trans></p>
                    {btw_attest
                    ?
                    <>
                    <p><Trans>Er is al een BTW-attest voor deze interventie.</Trans></p>
                    </>
                    :
                    <>
                    <div className='flex items-center gap-2'>
                        <Switch checked={older_10years} onCheckedChange={(v)=>{setolder_10years(v)}} id='older-10-years'/>
                        <Label htmlFor='older-10-years'><Trans>Is het gebouw ouder dan 10 jaar?</Trans></Label>
                    </div>
                    {older_10years &&
                    <AddBTWAttest onSuccess={()=>{setopen(false)}}/>
                    }
                    </>
                    }
                </div>

                
            </div>
        </SheetContent>
    </Sheet>
  )
}

export default AddDocuments